import { WEDDING_DATE } from './weddingCountdown';

export type PhotoboothEffectId =
  | 'classic'
  | 'garden'
  | 'polaroid'
  | 'hearts'
  | 'stars'
  | 'carnival'
  | 'noir'
  | 'dawn'
  | 'moonlit'
  | 'champagne'
  | 'film'
  | 'pop'
  | 'cyber'
  | 'lace'
  | 'sakura'
  | 'silk'
  | 'crown'
  | 'glitch'
  | 'rainbow'
  | 'disco'
  | 'vhs'
  | 'ripple'
  | 'prism'
  | 'pixel'
  | 'kaleido'
  | 'pulse'
  | 'static'
  | 'tunnel'
  | 'shake'
  | 'melt'
  | 'shard'
  | 'echo'
  | 'rave'
  | 'halo'
  | 'spiral'
  | 'smear'
  | 'vertigo'
  | 'nebula'
  | 'orbit';

export type PhotoboothEffect = {
  id: PhotoboothEffectId;
  label: string;
  emoji: string;
};

export const PHOTOBOOTH_EFFECTS: PhotoboothEffect[] = [
  { id: 'classic', label: 'Naturel', emoji: '✨' },
  { id: 'noir', label: 'Noir & blanc', emoji: '🎞️' },
  { id: 'polaroid', label: 'Polaroid', emoji: '📸' },
  { id: 'film', label: 'Pellicule', emoji: '🎬' },
  { id: 'cyber', label: 'Néon', emoji: '💫' },
  { id: 'stars', label: 'Étoilé', emoji: '⭐' },
  { id: 'hearts', label: 'Cœurs', emoji: '💚' },
  { id: 'sakura', label: 'Sakura', emoji: '🌸' },
  { id: 'moonlit', label: 'Clair de lune', emoji: '🌙' },
  { id: 'kaleido', label: 'Kaléidoscope', emoji: '🔷' },
  { id: 'garden', label: 'Jardin', emoji: '🌿' },
  { id: 'carnival', label: 'Carnaval', emoji: '🎭' },
  { id: 'dawn', label: 'Aurore', emoji: '🌅' },
  { id: 'champagne', label: 'Champagne', emoji: '🥂' },
  { id: 'pop', label: 'Pop art', emoji: '💥' },
  { id: 'lace', label: 'Dentelle', emoji: '🪷' },
  { id: 'silk', label: 'Soie', emoji: '🧵' },
  { id: 'crown', label: 'Couronne', emoji: '👑' },
  { id: 'glitch', label: 'Glitch', emoji: '📺' },
  { id: 'rainbow', label: 'Arc-en-ciel', emoji: '🌈' },
  { id: 'disco', label: 'Disco', emoji: '🪩' },
  { id: 'vhs', label: 'Magnétoscope', emoji: '📼' },
  { id: 'ripple', label: 'Ondes', emoji: '🌊' },
  { id: 'prism', label: 'Prisme', emoji: '🔮' },
  { id: 'pixel', label: 'Pixel', emoji: '👾' },
  { id: 'pulse', label: 'Pulsation', emoji: '💓' },
  { id: 'static', label: 'Parasites', emoji: '📡' },
  { id: 'tunnel', label: 'Tunnel', emoji: '🌀' },
  { id: 'shake', label: 'Secousses', emoji: '⚡' },
  { id: 'melt', label: 'Fonte', emoji: '🫠' },
  { id: 'shard', label: 'Éclats', emoji: '💎' },
  { id: 'echo', label: 'Écho', emoji: '👻' },
  { id: 'rave', label: 'Rave', emoji: '🎧' },
  { id: 'halo', label: 'Halo', emoji: '⭕' },
  { id: 'spiral', label: 'Spirale', emoji: '🌪️' },
  { id: 'smear', label: 'Traînées', emoji: '💨' },
  { id: 'vertigo', label: 'Vertige', emoji: '😵' },
  { id: 'nebula', label: 'Nébuleuse', emoji: '🌌' },
  { id: 'orbit', label: 'Orbite', emoji: '🛸' },
];

/** Effets retirés du sélecteur (désactivation en dur). */
export const DISABLED_PHOTOBOOTH_EFFECT_IDS: readonly PhotoboothEffectId[] = [
  'garden',
  'carnival',
  'dawn',
  'champagne',
  'silk',
  'lace',
  'glitch',
  'disco',
  'vhs',
  'ripple',
  'prism',
  'pixel',
  'pulse',
  'static',
  'tunnel',
  'shake',
  'melt',
  'shard',
  'echo',
  'rave',
  'halo',
  'spiral',
  'smear',
  'vertigo',
  'nebula',
  'orbit',
  'crown',
  'rainbow',
  'pop',
];

const DISABLED_PHOTOBOOTH_EFFECT_SET = new Set<PhotoboothEffectId>(DISABLED_PHOTOBOOTH_EFFECT_IDS);

export function isPhotoboothEffectEnabled(id: PhotoboothEffectId): boolean {
  return !DISABLED_PHOTOBOOTH_EFFECT_SET.has(id);
}

export const ENABLED_PHOTOBOOTH_EFFECTS = PHOTOBOOTH_EFFECTS.filter((effect) =>
  isPhotoboothEffectEnabled(effect.id)
);

export const DEFAULT_PHOTOBOOTH_EFFECT_ID: PhotoboothEffectId = 'classic';

type RenderOptions = {
  mirror: boolean;
  effectId: PhotoboothEffectId;
  time?: number;
};

type OverlayFn = (
  ctx: CanvasRenderingContext2D,
  width: number,
  height: number,
  time: number
) => void;

const EFFECT_FILTERS: Record<PhotoboothEffectId, string | null> = {
  classic: null,
  garden: 'sepia(0.22) saturate(1.15) hue-rotate(-12deg) contrast(1.04)',
  polaroid: null,
  hearts: null,
  stars: 'saturate(1.08) contrast(1.08) brightness(0.98)',
  carnival: 'saturate(1.45) contrast(1.12) brightness(1.03)',
  noir: 'grayscale(1) contrast(1.28) brightness(0.96)',
  dawn: 'sepia(0.35) saturate(1.45) hue-rotate(-8deg) brightness(1.06)',
  moonlit: 'saturate(0.65) hue-rotate(195deg) brightness(0.78) contrast(1.12)',
  champagne: 'sepia(0.42) saturate(1.35) brightness(1.1) contrast(1.05)',
  film: 'contrast(1.08) saturate(0.92) brightness(0.95)',
  pop: 'contrast(1.55) saturate(2) brightness(1.02)',
  cyber: 'saturate(1.55) hue-rotate(95deg) contrast(1.18) brightness(1.04)',
  lace: 'saturate(1.05) contrast(1.02)',
  sakura: 'saturate(1.12) hue-rotate(-8deg) brightness(1.03)',
  silk: 'saturate(0.92) brightness(1.08) contrast(0.98)',
  crown: 'saturate(1.1) contrast(1.06) brightness(1.02)',
  glitch: 'saturate(1.2) contrast(1.15)',
  rainbow: 'saturate(1.25) contrast(1.05)',
  disco: 'saturate(1.35) contrast(1.1) brightness(1.05)',
  vhs: 'saturate(1.15) contrast(1.08) brightness(0.94)',
  ripple: 'saturate(1.08) contrast(1.05)',
  prism: 'saturate(1.3) contrast(1.12) brightness(1.03)',
  pixel: 'contrast(1.2) saturate(1.35)',
  kaleido: 'saturate(1.25) contrast(1.1)',
  pulse: 'saturate(1.12) contrast(1.08)',
  static: 'contrast(1.2) brightness(0.92)',
  tunnel: 'saturate(1.2) contrast(1.15) brightness(0.95)',
  shake: 'saturate(1.15) contrast(1.12)',
  melt: 'saturate(1.1) contrast(1.08)',
  shard: 'saturate(1.2) contrast(1.15)',
  echo: 'saturate(0.9) contrast(1.05) brightness(0.98)',
  rave: 'saturate(1.55) contrast(1.18) brightness(1.04)',
  halo: 'saturate(1.2) contrast(1.1) brightness(1.03)',
  spiral: 'saturate(1.15) contrast(1.12)',
  smear: 'saturate(1.1) contrast(1.08) brightness(0.98)',
  vertigo: 'saturate(1.18) contrast(1.14)',
  nebula: 'saturate(1.35) hue-rotate(25deg) contrast(1.1)',
  orbit: 'saturate(1.22) contrast(1.1) brightness(1.02)',
};

function drawMirroredImage(
  ctx: CanvasRenderingContext2D,
  source: CanvasImageSource,
  width: number,
  height: number,
  mirror: boolean
): void {
  ctx.save();
  if (mirror) {
    ctx.translate(width, 0);
    ctx.scale(-1, 1);
  }
  ctx.drawImage(source, 0, 0, width, height);
  ctx.restore();
}

function snapshotCanvas(source: CanvasImageSource, width: number, height: number): HTMLCanvasElement {
  const canvas = document.createElement('canvas');
  canvas.width = width;
  canvas.height = height;
  const snapshotCtx = canvas.getContext('2d');
  if (snapshotCtx) {
    snapshotCtx.drawImage(source, 0, 0, width, height);
  }
  return canvas;
}

function redistributeRows(
  ctx: CanvasRenderingContext2D,
  width: number,
  height: number,
  temp: CanvasImageSource,
  offsetForY: (y: number, time: number) => number,
  time: number,
  sliceHeight = 4
): void {
  ctx.clearRect(0, 0, width, height);
  for (let y = 0; y < height; y += sliceHeight) {
    const slice = Math.min(sliceHeight, height - y);
    ctx.drawImage(temp, 0, y, width, slice, offsetForY(y, time), y, width, slice);
  }
}

function applyScaleFromCenter(
  ctx: CanvasRenderingContext2D,
  width: number,
  height: number,
  temp: CanvasImageSource,
  scale: number
): void {
  ctx.clearRect(0, 0, width, height);
  ctx.save();
  ctx.translate(width / 2, height / 2);
  ctx.scale(scale, scale);
  ctx.translate(-width / 2, -height / 2);
  ctx.drawImage(temp, 0, 0, width, height);
  ctx.restore();
}

function drawVignette(ctx: CanvasRenderingContext2D, width: number, height: number, strength: number): void {
  const radius = Math.max(width, height) * 0.85;
  const gradient = ctx.createRadialGradient(
    width / 2,
    height / 2,
    Math.min(width, height) * 0.18,
    width / 2,
    height / 2,
    radius
  );
  gradient.addColorStop(0, 'rgba(0, 0, 0, 0)');
  gradient.addColorStop(1, `rgba(0, 0, 0, ${strength})`);
  ctx.fillStyle = gradient;
  ctx.fillRect(0, 0, width, height);
}

function drawHeart(ctx: CanvasRenderingContext2D, x: number, y: number, size: number, alpha: number): void {
  ctx.save();
  ctx.globalAlpha = alpha;
  ctx.fillStyle = '#c44b6d';
  ctx.beginPath();
  const top = size * 0.3;
  ctx.moveTo(x, y + top);
  ctx.bezierCurveTo(x, y, x - size / 2, y, x - size / 2, y + top);
  ctx.bezierCurveTo(x - size / 2, y + (size + top) / 2, x, y + (size + top) / 1.2, x, y + size);
  ctx.bezierCurveTo(x, y + (size + top) / 1.2, x + size / 2, y + (size + top) / 2, x + size / 2, y + top);
  ctx.bezierCurveTo(x + size / 2, y, x, y, x, y + top);
  ctx.closePath();
  ctx.fill();
  ctx.restore();
}

function drawStar(ctx: CanvasRenderingContext2D, x: number, y: number, radius: number, alpha: number): void {
  ctx.save();
  ctx.globalAlpha = alpha;
  ctx.fillStyle = '#f4d35e';
  ctx.beginPath();
  for (let i = 0; i < 5; i += 1) {
    const angle = (Math.PI * 2 * i) / 5 - Math.PI / 2;
    const outerX = x + Math.cos(angle) * radius;
    const outerY = y + Math.sin(angle) * radius;
    if (i === 0) ctx.moveTo(outerX, outerY);
    else ctx.lineTo(outerX, outerY);
    const innerAngle = angle + Math.PI / 5;
    ctx.lineTo(x + Math.cos(innerAngle) * radius * 0.42, y + Math.sin(innerAngle) * radius * 0.42);
  }
  ctx.closePath();
  ctx.fill();
  ctx.restore();
}

function drawHeartsOverlay(ctx: CanvasRenderingContext2D, width: number, height: number, time: number): void {
  const seeds = [
    [0.12, 0.18, 0.9],
    [0.82, 0.22, 1.1],
    [0.2, 0.72, 0.75],
    [0.78, 0.68, 0.95],
    [0.5, 0.08, 0.7],
    [0.08, 0.48, 0.8],
    [0.9, 0.5, 0.85],
  ];
  for (const [nx, ny, scale] of seeds) {
    const drift = Math.sin(time * 1.4 + nx * 10) * 12;
    const size = Math.min(width, height) * 0.08 * scale;
    drawHeart(ctx, width * nx, height * ny + drift, size, 0.28);
  }
  drawVignette(ctx, width, height, 0.35);
}

function starHash(index: number, salt: number): number {
  const n = Math.sin(index * 12.9898 + salt * 78.233) * 43758.5453;
  return n - Math.floor(n);
}

function starPerimeterPoint(
  width: number,
  height: number,
  t: number,
  inset: number,
  scatter: number,
  seed: number
): [number, number] {
  const innerW = width - inset * 2;
  const innerH = height - inset * 2;
  const perimeter = 2 * (innerW + innerH);
  let distance = ((t % 1) + 1) % 1 * perimeter;

  let x: number;
  let y: number;

  if (distance < innerW) {
    x = inset + distance;
    y = inset;
  } else if ((distance -= innerW) < innerH) {
    x = inset + innerW;
    y = inset + distance;
  } else if ((distance -= innerH) < innerW) {
    x = inset + innerW - distance;
    y = inset + innerH;
  } else {
    distance -= innerW;
    x = inset;
    y = inset + innerH - distance;
  }

  const jitterX = (starHash(seed, 5) - 0.5) * scatter;
  const jitterY = (starHash(seed, 6) - 0.5) * scatter;

  return [x + jitterX, y + jitterY];
}

function drawStarsOverlay(ctx: CanvasRenderingContext2D, width: number, height: number, time: number): void {
  const scatter = Math.min(width, height) * 0.028;
  const minInset = Math.min(width, height) * 0.008;
  const maxInset = Math.min(width, height) * 0.14;

  for (let i = 0; i < 30; i += 1) {
    const phase = time * 2 + i * 1.7;
    const t = starHash(i, 1) + starHash(i, 2) * 0.07;
    const inset = minInset + starHash(i, 3) * (maxInset - minInset);
    const [x, y] = starPerimeterPoint(width, height, t, inset, scatter, i);
    const twinkle = 0.25 + (Math.sin(phase) + 1) * 0.22;
    const radius = Math.min(width, height) * (0.01 + starHash(i, 4) * 0.008);
    drawStar(ctx, x, y, radius, twinkle);
  }
  const glow = ctx.createLinearGradient(0, 0, 0, height);
  glow.addColorStop(0, 'rgba(255, 230, 150, 0.12)');
  glow.addColorStop(1, 'rgba(9, 82, 40, 0.18)');
  ctx.fillStyle = glow;
  ctx.fillRect(0, 0, width, height);
}

function drawCarnivalOverlay(ctx: CanvasRenderingContext2D, width: number, height: number, time: number): void {
  for (let i = 0; i < 28; i += 1) {
    const t = time * 0.55 + i * 0.4;
    const x = (width * ((i * 37) % 100)) / 100;
    const y = ((t * 90 + i * 31) % (height + 40)) - 20;
    const size = 5 + (i % 3) * 2;
    ctx.save();
    ctx.globalAlpha = 0.55;
    ctx.fillStyle = i % 3 === 0 ? '#e85d75' : i % 3 === 1 ? '#f4d35e' : '#4ecdc4';
    ctx.translate(x, y);
    ctx.rotate(t + i);
    ctx.fillRect(-size / 2, -size / 4, size, size / 2);
    ctx.restore();
  }

  ctx.save();
  ctx.globalAlpha = 0.85;
  ctx.fillStyle = '#095228';
  for (const [x1, y1, x2, y2, x3, y3] of [
    [0, 0, width * 0.34, 0, 0, height * 0.34],
    [width, 0, width * 0.66, 0, width, height * 0.34],
    [0, height, 0, height * 0.66, width * 0.34, height],
    [width, height, width, height * 0.66, width * 0.66, height],
  ]) {
    ctx.beginPath();
    ctx.moveTo(x1, y1);
    ctx.lineTo(x2, y2);
    ctx.lineTo(x3, y3);
    ctx.closePath();
    ctx.fill();
  }
  ctx.restore();
}

function drawPhotoProps(ctx: CanvasRenderingContext2D, width: number, height: number): void {
  const centerX = width / 2;
  const eyeY = height * 0.42;
  const eyeOffset = width * 0.12;
  const eyeW = width * 0.14;
  const eyeH = height * 0.05;

  ctx.save();
  ctx.strokeStyle = 'rgba(20, 20, 20, 0.82)';
  ctx.lineWidth = Math.max(3, width * 0.008);
  ctx.lineCap = 'round';
  ctx.beginPath();
  ctx.ellipse(centerX - eyeOffset, eyeY, eyeW, eyeH, 0, 0, Math.PI * 2);
  ctx.stroke();
  ctx.beginPath();
  ctx.ellipse(centerX + eyeOffset, eyeY, eyeW, eyeH, 0, 0, Math.PI * 2);
  ctx.stroke();
  ctx.beginPath();
  ctx.moveTo(centerX - eyeOffset - eyeW * 0.9, eyeY);
  ctx.quadraticCurveTo(centerX, eyeY - eyeH * 2.2, centerX + eyeOffset + eyeW * 0.9, eyeY);
  ctx.stroke();

  const lipY = height * 0.58;
  ctx.fillStyle = 'rgba(210, 70, 95, 0.55)';
  ctx.beginPath();
  ctx.ellipse(centerX, lipY, width * 0.09, height * 0.028, 0, 0, Math.PI * 2);
  ctx.fill();

  ctx.strokeStyle = 'rgba(40, 30, 20, 0.9)';
  ctx.lineWidth = Math.max(4, width * 0.01);
  const mustacheY = height * 0.54;
  ctx.beginPath();
  ctx.moveTo(centerX - width * 0.16, mustacheY);
  ctx.quadraticCurveTo(centerX - width * 0.04, mustacheY + height * 0.04, centerX, mustacheY + height * 0.01);
  ctx.quadraticCurveTo(centerX + width * 0.04, mustacheY + height * 0.04, centerX + width * 0.16, mustacheY);
  ctx.stroke();
  ctx.restore();
}

function drawDawnOverlay(ctx: CanvasRenderingContext2D, width: number, height: number): void {
  const gradient = ctx.createLinearGradient(0, 0, 0, height);
  gradient.addColorStop(0, 'rgba(255, 170, 90, 0.35)');
  gradient.addColorStop(0.45, 'rgba(255, 120, 140, 0.12)');
  gradient.addColorStop(1, 'rgba(9, 82, 40, 0.08)');
  ctx.fillStyle = gradient;
  ctx.fillRect(0, 0, width, height);
  drawVignette(ctx, width, height, 0.28);
}

function drawMoonlitOverlay(ctx: CanvasRenderingContext2D, width: number, height: number, time: number): void {
  ctx.fillStyle = 'rgba(40, 60, 140, 0.22)';
  ctx.fillRect(0, 0, width, height);
  for (let i = 0; i < 10; i += 1) {
    const twinkle = 0.15 + (Math.sin(time * 1.5 + i) + 1) * 0.12;
    drawStar(ctx, width * (0.1 + i * 0.08), height * (0.08 + (i % 3) * 0.04), width * 0.01, twinkle);
  }
  drawVignette(ctx, width, height, 0.5);
}

function drawChampagneOverlay(ctx: CanvasRenderingContext2D, width: number, height: number, time: number): void {
  for (let i = 0; i < 24; i += 1) {
    const phase = time * 0.8 + i * 0.6;
    const x = width * (((i * 41) % 100) / 100);
    const y = height - ((phase * 60 + i * 20) % (height * 1.1));
    const radius = 2 + (i % 4);
    ctx.save();
    ctx.globalAlpha = 0.35;
    ctx.fillStyle = '#f4d35e';
    ctx.beginPath();
    ctx.arc(x, y, radius, 0, Math.PI * 2);
    ctx.fill();
    ctx.restore();
  }
  ctx.fillStyle = 'rgba(255, 230, 160, 0.1)';
  ctx.fillRect(0, 0, width, height);
}

function drawFilmOverlay(ctx: CanvasRenderingContext2D, width: number, height: number, time: number): void {
  const bar = height * 0.08;
  ctx.fillStyle = 'rgba(0, 0, 0, 0.72)';
  ctx.fillRect(0, 0, width, bar);
  ctx.fillRect(0, height - bar, width, bar);
  ctx.fillStyle = 'rgba(255, 255, 255, 0.08)';
  for (let x = 0; x < width; x += width * 0.06) {
    ctx.fillRect(x, bar * 0.35, width * 0.02, bar * 0.3);
    ctx.fillRect(x, height - bar * 0.65, width * 0.02, bar * 0.3);
  }
  for (let i = 0; i < 80; i += 1) {
    const n = Math.sin(time * 20 + i * 12.7) * 0.5 + 0.5;
    ctx.fillStyle = `rgba(255, 255, 255, ${n * 0.05})`;
    ctx.fillRect((i * 73) % width, (i * 41) % height, 1, 1);
  }
}

function drawPopOverlay(ctx: CanvasRenderingContext2D, width: number, height: number): void {
  const step = Math.max(6, Math.min(width, height) * 0.02);
  ctx.save();
  ctx.globalAlpha = 0.18;
  ctx.fillStyle = '#095228';
  for (let y = 0; y < height; y += step) {
    for (let x = 0; x < width; x += step) {
      const dx = x + ((y / step) % 2) * (step / 2);
      ctx.beginPath();
      ctx.arc(dx, y, step * 0.22, 0, Math.PI * 2);
      ctx.fill();
    }
  }
  ctx.restore();
  ctx.strokeStyle = 'rgba(255, 255, 255, 0.85)';
  ctx.lineWidth = Math.max(4, width * 0.008);
  ctx.strokeRect(width * 0.04, height * 0.04, width * 0.92, height * 0.92);
}

function drawCyberOverlay(ctx: CanvasRenderingContext2D, width: number, height: number, time: number): void {
  ctx.save();
  ctx.globalAlpha = 0.12;
  ctx.fillStyle = '#00ffd5';
  for (let y = 0; y < height; y += 4) {
    if ((y + Math.floor(time * 40)) % 12 === 0) {
      ctx.fillRect(0, y, width, 1);
    }
  }
  ctx.restore();
  ctx.strokeStyle = 'rgba(255, 0, 200, 0.55)';
  ctx.lineWidth = Math.max(2, width * 0.004);
  ctx.strokeRect(width * 0.03, height * 0.03, width * 0.94, height * 0.94);
  ctx.strokeStyle = 'rgba(0, 255, 213, 0.45)';
  ctx.strokeRect(width * 0.06, height * 0.06, width * 0.88, height * 0.88);
}

function drawLaceOverlay(ctx: CanvasRenderingContext2D, width: number, height: number): void {
  const inset = Math.min(width, height) * 0.06;
  ctx.save();
  ctx.strokeStyle = 'rgba(255, 255, 255, 0.55)';
  ctx.lineWidth = Math.max(2, width * 0.003);
  for (const [sx, sy] of [
    [inset, inset],
    [width - inset, inset],
    [inset, height - inset],
    [width - inset, height - inset],
  ]) {
    ctx.beginPath();
    ctx.arc(sx, sy, inset * 0.9, 0, Math.PI * 2);
    ctx.stroke();
    ctx.beginPath();
    ctx.arc(sx, sy, inset * 0.55, 0, Math.PI * 2);
    ctx.stroke();
  }
  ctx.restore();
  drawVignette(ctx, width, height, 0.25);
}

function drawSakuraOverlay(ctx: CanvasRenderingContext2D, width: number, height: number, time: number): void {
  for (let i = 0; i < 22; i += 1) {
    const t = time * 0.35 + i * 0.5;
    const x = (width * ((i * 29) % 100)) / 100 + Math.sin(t * 2) * 18;
    const y = ((t * 70 + i * 25) % (height + 30)) - 15;
    ctx.save();
    ctx.globalAlpha = 0.42;
    ctx.fillStyle = i % 2 === 0 ? '#f7b4c8' : '#ffd6e3';
    ctx.translate(x, y);
    ctx.rotate(t + i);
    ctx.beginPath();
    ctx.ellipse(0, 0, 21, 12, 0, 0, Math.PI * 2);
    ctx.fill();
    ctx.restore();
  }
}

function drawSilkOverlay(ctx: CanvasRenderingContext2D, width: number, height: number, time: number): void {
  for (let i = 0; i < 14; i += 1) {
    const pulse = 0.08 + (Math.sin(time * 0.9 + i) + 1) * 0.06;
    const x = width * (((i * 61) % 100) / 100);
    const y = height * (((i * 37) % 100) / 100);
    const radius = Math.min(width, height) * (0.08 + (i % 3) * 0.03);
    const gradient = ctx.createRadialGradient(x, y, 0, x, y, radius);
    gradient.addColorStop(0, `rgba(255, 255, 255, ${pulse})`);
    gradient.addColorStop(1, 'rgba(255, 255, 255, 0)');
    ctx.fillStyle = gradient;
    ctx.fillRect(x - radius, y - radius, radius * 2, radius * 2);
  }
}

function drawCrownOverlay(ctx: CanvasRenderingContext2D, width: number, height: number): void {
  const cx = width / 2;
  const baseY = height * 0.1;
  const crownW = width * 0.36;
  ctx.save();
  ctx.fillStyle = 'rgba(244, 211, 94, 0.88)';
  ctx.strokeStyle = 'rgba(19, 45, 34, 0.5)';
  ctx.lineWidth = Math.max(2, width * 0.003);
  ctx.beginPath();
  ctx.moveTo(cx - crownW / 2, baseY + crownW * 0.2);
  ctx.lineTo(cx - crownW * 0.35, baseY);
  ctx.lineTo(cx - crownW * 0.18, baseY + crownW * 0.16);
  ctx.lineTo(cx, baseY - crownW * 0.08);
  ctx.lineTo(cx + crownW * 0.18, baseY + crownW * 0.16);
  ctx.lineTo(cx + crownW * 0.35, baseY);
  ctx.lineTo(cx + crownW / 2, baseY + crownW * 0.2);
  ctx.closePath();
  ctx.fill();
  ctx.stroke();
  ctx.restore();
  drawVignette(ctx, width, height, 0.22);
}

function drawGlitchOverlay(ctx: CanvasRenderingContext2D, width: number, height: number, time: number): void {
  const slice = Math.max(8, Math.floor(height / 24));
  for (let y = 0; y < height; y += slice) {
    if (Math.sin(time * 8 + y * 0.08) > 0.65) {
      const offset = Math.sin(time * 12 + y) * width * 0.02;
      ctx.drawImage(ctx.canvas, 0, y, width, slice, offset, y, width, slice);
    }
  }
  ctx.fillStyle = 'rgba(0, 255, 255, 0.06)';
  ctx.fillRect(width * 0.02, 0, width * 0.2, height);
  ctx.fillStyle = 'rgba(255, 0, 180, 0.06)';
  ctx.fillRect(width * 0.78, 0, width * 0.2, height);
}

function drawRainbowOverlay(ctx: CanvasRenderingContext2D, width: number, height: number, time: number): void {
  const colors = ['#ff5a5f', '#ffb347', '#f4d35e', '#6a9f80', '#4ecdc4', '#7b68ee', '#e056fd'];
  ctx.save();
  ctx.globalAlpha = 0.22;
  for (let i = 0; i < colors.length; i += 1) {
    const y = height * 0.08 + i * height * 0.035 + Math.sin(time + i) * 4;
    ctx.fillStyle = colors[i];
    ctx.fillRect(0, y, width, height * 0.028);
  }
  ctx.restore();
  ctx.fillStyle = 'rgba(255, 255, 255, 0.08)';
  ctx.fillRect(0, 0, width, height);
}

function drawDiscoOverlay(ctx: CanvasRenderingContext2D, width: number, height: number, time: number): void {
  const spots = 10;
  for (let i = 0; i < spots; i += 1) {
    const angle = time * 0.9 + (Math.PI * 2 * i) / spots;
    const x = width / 2 + Math.cos(angle) * width * 0.32;
    const y = height / 2 + Math.sin(angle * 1.3) * height * 0.28;
    const radius = Math.min(width, height) * 0.14;
    const gradient = ctx.createRadialGradient(x, y, 0, x, y, radius);
    const hue = (i * 36 + time * 40) % 360;
    gradient.addColorStop(0, `hsla(${hue}, 85%, 65%, 0.35)`);
    gradient.addColorStop(1, 'rgba(0, 0, 0, 0)');
    ctx.fillStyle = gradient;
    ctx.fillRect(x - radius, y - radius, radius * 2, radius * 2);
  }
}

function drawVhsOverlay(ctx: CanvasRenderingContext2D, width: number, height: number, time: number): void {
  const snap = snapshotCanvas(ctx.canvas, width, height);
  redistributeRows(ctx, width, height, snap, (y) => Math.sin(time * 6 + y * 0.05) * width * 0.012, time, 3);
  ctx.save();
  ctx.globalAlpha = 0.16;
  ctx.fillStyle = '#fff';
  for (let y = 0; y < height; y += 3) {
    if ((y + Math.floor(time * 50)) % 9 === 0) ctx.fillRect(0, y, width, 1);
  }
  ctx.restore();
  const bandY = (time * 120) % height;
  ctx.fillStyle = 'rgba(255, 255, 255, 0.08)';
  ctx.fillRect(0, bandY, width, height * 0.08);
}

function drawRippleOverlay(ctx: CanvasRenderingContext2D, width: number, height: number, time: number): void {
  const snap = snapshotCanvas(ctx.canvas, width, height);
  redistributeRows(
    ctx,
    width,
    height,
    snap,
    (y) => Math.sin(y * 0.06 + time * 4) * 18 + Math.sin(y * 0.02 + time * 1.5) * 8,
    time,
    4
  );
}

function drawPrismOverlay(ctx: CanvasRenderingContext2D, width: number, height: number, time: number): void {
  const snap = snapshotCanvas(ctx.canvas, width, height);
  const shift = 6 + Math.sin(time * 2.5) * 4;
  ctx.save();
  ctx.globalCompositeOperation = 'screen';
  ctx.globalAlpha = 0.45;
  ctx.drawImage(snap, -shift, 0);
  ctx.globalAlpha = 0.35;
  ctx.drawImage(snap, shift, 0);
  ctx.restore();
  ctx.save();
  ctx.globalCompositeOperation = 'lighter';
  ctx.globalAlpha = 0.22;
  ctx.fillStyle = 'rgba(255, 0, 120, 0.35)';
  ctx.fillRect(0, 0, width, height);
  ctx.fillStyle = 'rgba(0, 255, 220, 0.28)';
  ctx.fillRect(0, 0, width, height);
  ctx.restore();
}

function drawPixelOverlay(ctx: CanvasRenderingContext2D, width: number, height: number): void {
  const snap = snapshotCanvas(ctx.canvas, width, height);
  const block = Math.max(6, Math.floor(Math.min(width, height) * 0.035));
  const pixelW = Math.max(1, Math.floor(width / block));
  const pixelH = Math.max(1, Math.floor(height / block));
  const micro = document.createElement('canvas');
  micro.width = pixelW;
  micro.height = pixelH;
  const microCtx = micro.getContext('2d');
  if (!microCtx) return;
  microCtx.imageSmoothingEnabled = false;
  microCtx.drawImage(snap, 0, 0, pixelW, pixelH);
  ctx.clearRect(0, 0, width, height);
  ctx.imageSmoothingEnabled = false;
  ctx.drawImage(micro, 0, 0, pixelW, pixelH, 0, 0, width, height);
  ctx.imageSmoothingEnabled = true;
}

function drawKaleidoOverlay(ctx: CanvasRenderingContext2D, width: number, height: number): void {
  const snap = snapshotCanvas(ctx.canvas, width, height);
  const halfW = width / 2;
  const halfH = height / 2;
  ctx.clearRect(0, 0, width, height);
  ctx.drawImage(snap, 0, 0, halfW, halfH, 0, 0, halfW, halfH);
  ctx.save();
  ctx.translate(width, 0);
  ctx.scale(-1, 1);
  ctx.drawImage(snap, 0, 0, halfW, halfH, 0, 0, halfW, halfH);
  ctx.restore();
  ctx.save();
  ctx.translate(0, height);
  ctx.scale(1, -1);
  ctx.drawImage(snap, 0, 0, halfW, halfH, 0, 0, halfW, halfH);
  ctx.restore();
  ctx.save();
  ctx.translate(width, height);
  ctx.scale(-1, -1);
  ctx.drawImage(snap, 0, 0, halfW, halfH, 0, 0, halfW, halfH);
  ctx.restore();
}

function drawPulseOverlay(ctx: CanvasRenderingContext2D, width: number, height: number, time: number): void {
  const snap = snapshotCanvas(ctx.canvas, width, height);
  const scale = 1 + Math.sin(time * 3.2) * 0.06;
  applyScaleFromCenter(ctx, width, height, snap, scale);
  ctx.save();
  ctx.globalAlpha = 0.12;
  ctx.strokeStyle = '#fff';
  ctx.lineWidth = Math.max(2, width * 0.004);
  ctx.strokeRect(width * 0.04, height * 0.04, width * 0.92, height * 0.92);
  ctx.restore();
}

function drawStaticOverlay(ctx: CanvasRenderingContext2D, width: number, height: number, time: number): void {
  const snap = snapshotCanvas(ctx.canvas, width, height);
  redistributeRows(
    ctx,
    width,
    height,
    snap,
    (y) => (Math.sin(time * 18 + y * 0.2) > 0.82 ? Math.sin(time * 40 + y) * width * 0.03 : 0),
    time,
    5
  );
  for (let i = 0; i < 220; i += 1) {
    const n = Math.sin(time * 30 + i * 9.1) * 0.5 + 0.5;
    ctx.fillStyle = `rgba(255, 255, 255, ${n * 0.12})`;
    ctx.fillRect((i * 53) % width, (i * 97) % height, 1, 1);
  }
}

function drawTunnelOverlay(ctx: CanvasRenderingContext2D, width: number, height: number, time: number): void {
  const snap = snapshotCanvas(ctx.canvas, width, height);
  ctx.clearRect(0, 0, width, height);
  const bands = 14;
  for (let i = 0; i < bands; i += 1) {
    const t = (i / bands + time * 0.15) % 1;
    const scale = 0.35 + t * 0.75;
    const drawW = width * scale;
    const drawH = height * scale;
    const x = (width - drawW) / 2;
    const y = (height - drawH) / 2;
    ctx.save();
    ctx.globalAlpha = 0.55 + (1 - t) * 0.35;
    ctx.drawImage(snap, x, y, drawW, drawH);
    ctx.restore();
  }
}

function drawShakeOverlay(ctx: CanvasRenderingContext2D, width: number, height: number, time: number): void {
  const snap = snapshotCanvas(ctx.canvas, width, height);
  const amplitude = width * 0.03;
  redistributeRows(
    ctx,
    width,
    height,
    snap,
    (y) => Math.sin(time * 22 + y * 0.11) * amplitude + Math.cos(time * 17 + y * 0.07) * amplitude * 0.5,
    time,
    6
  );
}

function drawMeltOverlay(ctx: CanvasRenderingContext2D, width: number, height: number, time: number): void {
  const snap = snapshotCanvas(ctx.canvas, width, height);
  redistributeRows(
    ctx,
    width,
    height,
    snap,
    (y) => Math.pow(y / height, 1.6) * (Math.sin(time * 2 + y * 0.01) * 14 + 10),
    time,
    5
  );
  const drip = ctx.createLinearGradient(0, height * 0.55, 0, height);
  drip.addColorStop(0, 'rgba(255, 255, 255, 0)');
  drip.addColorStop(1, 'rgba(255, 255, 255, 0.12)');
  ctx.fillStyle = drip;
  ctx.fillRect(0, 0, width, height);
}

function drawShardOverlay(ctx: CanvasRenderingContext2D, width: number, height: number, time: number): void {
  const snap = snapshotCanvas(ctx.canvas, width, height);
  ctx.clearRect(0, 0, width, height);
  const shards = 10;
  for (let i = 0; i < shards; i += 1) {
    const col = i % 5;
    const row = Math.floor(i / 5);
    const sx = (col / 5) * width;
    const sy = (row / 2) * height;
    const sw = width / 5;
    const sh = height / 2;
    const dx = Math.sin(time * 3 + i * 1.7) * width * 0.02;
    const dy = Math.cos(time * 2.5 + i * 1.3) * height * 0.02;
    ctx.drawImage(snap, sx, sy, sw, sh, sx + dx, sy + dy, sw, sh);
  }
  ctx.strokeStyle = 'rgba(255, 255, 255, 0.18)';
  ctx.lineWidth = 1;
  for (let i = 1; i < 5; i += 1) {
    const x = (width / 5) * i;
    ctx.beginPath();
    ctx.moveTo(x, 0);
    ctx.lineTo(x, height);
    ctx.stroke();
  }
  ctx.beginPath();
  ctx.moveTo(0, height / 2);
  ctx.lineTo(width, height / 2);
  ctx.stroke();
}

function drawEchoOverlay(ctx: CanvasRenderingContext2D, width: number, height: number, time: number): void {
  const snap = snapshotCanvas(ctx.canvas, width, height);
  ctx.clearRect(0, 0, width, height);
  ctx.drawImage(snap, 0, 0);

  const driftX = Math.sin(time * 2.1) * width * 0.052;
  const driftY = Math.cos(time * 1.6) * height * 0.038;
  const trailX = Math.cos(time * 1.7) * width * 0.068;
  const trailY = Math.sin(time * 1.3) * height * 0.048;

  ctx.save();
  ctx.globalAlpha = 0.42;
  ctx.translate(width / 2, height / 2);
  ctx.scale(1.06, 1.06);
  ctx.translate(-width / 2 + driftX, -height / 2 + driftY);
  ctx.drawImage(snap, 0, 0);
  ctx.restore();

  ctx.save();
  ctx.globalAlpha = 0.34;
  ctx.drawImage(snap, trailX, trailY);
  ctx.restore();

  ctx.save();
  ctx.globalAlpha = 0.24;
  ctx.drawImage(snap, -trailX * 0.72, -trailY * 0.72);
  ctx.restore();
}

function drawRaveOverlay(ctx: CanvasRenderingContext2D, width: number, height: number, time: number): void {
  const snap = snapshotCanvas(ctx.canvas, width, height);
  redistributeRows(ctx, width, height, snap, (y) => Math.sin(time * 5 + y * 0.08) * width * 0.025, time, 2);
  ctx.save();
  ctx.globalCompositeOperation = 'overlay';
  const bands = 6;
  for (let i = 0; i < bands; i += 1) {
    const hue = (i * 60 + time * 80) % 360;
    const bandHeight = height / bands;
    const shift = Math.sin(time * 2 + i) * bandHeight * 0.5;
    ctx.fillStyle = `hsla(${hue}, 90%, 50%, 0.28)`;
    ctx.fillRect(0, i * bandHeight + shift, width, bandHeight);
  }
  ctx.restore();
}

function drawHaloOverlay(ctx: CanvasRenderingContext2D, width: number, height: number, time: number): void {
  const snap = snapshotCanvas(ctx.canvas, width, height);
  ctx.clearRect(0, 0, width, height);
  ctx.drawImage(snap, 0, 0);
  const cx = width / 2;
  const cy = height / 2;
  const rings = 5;
  for (let i = 0; i < rings; i += 1) {
    const radius = Math.min(width, height) * (0.18 + i * 0.11);
    const orbitAngle = time * (1.1 + i * 0.2) + i;
    const hx = cx + Math.cos(orbitAngle) * radius * 0.35;
    const hy = cy + Math.sin(orbitAngle * 1.2) * radius * 0.35;
    const hue = (i * 72 + time * 45) % 360;
    const gradient = ctx.createRadialGradient(hx, hy, radius * 0.35, hx, hy, radius);
    gradient.addColorStop(0, 'rgba(0, 0, 0, 0)');
    gradient.addColorStop(0.55, `hsla(${hue}, 80%, 70%, 0.45)`);
    gradient.addColorStop(1, 'rgba(0, 0, 0, 0)');
    ctx.fillStyle = gradient;
    ctx.beginPath();
    ctx.arc(cx, cy, radius, 0, Math.PI * 2);
    ctx.fill();
  }
}

function drawSpiralOverlay(ctx: CanvasRenderingContext2D, width: number, height: number, time: number): void {
  const snap = snapshotCanvas(ctx.canvas, width, height);
  redistributeRows(
    ctx,
    width,
    height,
    snap,
    (y) => {
      const norm = (y / height - 0.5) * 2;
      return Math.sin(time * 4 + norm * 10 + y * 0.02) * width * 0.02 * (1.1 - Math.abs(norm) * 0.6);
    },
    time,
    2
  );
}

function drawSmearOverlay(ctx: CanvasRenderingContext2D, width: number, height: number, time: number): void {
  const snap = snapshotCanvas(ctx.canvas, width, height);
  ctx.clearRect(0, 0, width, height);
  const trails = 6;
  for (let i = trails - 1; i >= 0; i -= 1) {
    const offset = Math.sin(time * 3) * width * 0.04 * (i / trails);
    ctx.globalAlpha = 0.12 + (trails - i) * 0.12;
    ctx.drawImage(snap, offset - i * 6, -i * 2);
  }
  ctx.globalAlpha = 1;
  ctx.drawImage(snap, 0, 0);
  ctx.save();
  ctx.globalCompositeOperation = 'screen';
  ctx.fillStyle = `hsla(${(time * 70) % 360}, 70%, 60%, 0.2)`;
  ctx.fillRect(Math.sin(time) * width * 0.1, 0, width * 0.35, height);
  ctx.restore();
}

function drawVertigoOverlay(ctx: CanvasRenderingContext2D, width: number, height: number, time: number): void {
  const snap = snapshotCanvas(ctx.canvas, width, height);
  ctx.clearRect(0, 0, width, height);
  ctx.save();
  ctx.translate(width / 2, height / 2);
  const scale = 1 + Math.sin(time * 2.5) * 0.08;
  ctx.rotate(Math.sin(time * 1.8) * 0.06);
  ctx.scale(scale, scale);
  ctx.translate(-width / 2, -height / 2);
  ctx.drawImage(snap, 0, 0);
  ctx.restore();
}

function drawNebulaOverlay(ctx: CanvasRenderingContext2D, width: number, height: number, time: number): void {
  const snap = snapshotCanvas(ctx.canvas, width, height);
  redistributeRows(
    ctx,
    width,
    height,
    snap,
    (y) => Math.sin(time * 2 + y * 0.035) * 14 + Math.cos(time * 1.3 + y * 0.08) * 8,
    time,
    5
  );
  ctx.save();
  ctx.globalCompositeOperation = 'screen';
  for (let i = 0; i < 4; i += 1) {
    const x = width * (0.2 + 0.2 * i) + Math.sin(time + i) * width * 0.15;
    const y = height * (0.35 + 0.15 * Math.cos(time * 0.8 + i));
    const radius = Math.min(width, height) * 0.28;
    const hue = 260 + i * 25 + time * 15;
    const gradient = ctx.createRadialGradient(x, y, 0, x, y, radius);
    gradient.addColorStop(0, `hsla(${hue}, 80%, 60%, 0.4)`);
    gradient.addColorStop(1, 'rgba(0, 0, 0, 0)');
    ctx.fillStyle = gradient;
    ctx.fillRect(x - radius, y - radius, radius * 2, radius * 2);
  }
  ctx.restore();
  drawDiscoOverlay(ctx, width, height, time * 0.6);
}

function drawOrbitOverlay(ctx: CanvasRenderingContext2D, width: number, height: number, time: number): void {
  const snap = snapshotCanvas(ctx.canvas, width, height);
  ctx.clearRect(0, 0, width, height);
  ctx.drawImage(snap, 0, 0);
  const clones = 5;
  const thumbW = width * 0.22;
  const thumbH = height * 0.22;
  for (let i = 0; i < clones; i += 1) {
    const angle = time * (1.5 + i * 0.15) + (Math.PI * 2 * i) / clones;
    const orbitR = Math.min(width, height) * 0.28;
    const x = width / 2 + Math.cos(angle) * orbitR - thumbW / 2;
    const y = height / 2 + Math.sin(angle * 1.1) * orbitR * 0.85 - thumbH / 2;
    const hue = (i * 50 + time * 40) % 360;
    ctx.save();
    ctx.globalAlpha = 0.55;
    ctx.drawImage(snap, x, y, thumbW, thumbH);
    ctx.restore();
    ctx.save();
    ctx.globalCompositeOperation = 'lighter';
    ctx.strokeStyle = `hsla(${hue}, 90%, 65%, 0.7)`;
    ctx.lineWidth = 2;
    ctx.strokeRect(x, y, thumbW, thumbH);
    ctx.restore();
  }
}

function formatPolaroidDate(): string {
  return WEDDING_DATE.toLocaleDateString('fr-FR', {
    day: 'numeric',
    month: 'long',
    year: 'numeric',
  });
}

function renderPolaroid(
  ctx: CanvasRenderingContext2D,
  source: CanvasImageSource,
  width: number,
  height: number,
  mirror: boolean
): void {
  const marginX = width * 0.07;
  const marginTop = height * 0.07;
  const marginBottom = height * 0.18;
  const innerW = width - marginX * 2;
  const innerH = height - marginTop - marginBottom;

  ctx.fillStyle = '#f7f2e8';
  ctx.fillRect(0, 0, width, height);

  ctx.save();
  ctx.beginPath();
  ctx.rect(marginX, marginTop, innerW, innerH);
  ctx.clip();
  ctx.filter = 'saturate(1.05) contrast(1.02)';
  drawMirroredImage(ctx, source, width, height, mirror);
  ctx.filter = 'none';
  ctx.restore();

  ctx.fillStyle = 'rgba(19, 45, 34, 0.72)';
  ctx.textAlign = 'center';

  const nameFontSize = Math.max(14, width * 0.035);
  ctx.font = `600 ${nameFontSize}px "Cormorant Garamond", Georgia, serif`;
  ctx.fillText('Virginie & Jean-Christophe', width / 2, height - marginBottom * 0.48);

  const dateFontSize = Math.max(12, width * 0.028);
  ctx.font = `500 ${dateFontSize}px "Cormorant Garamond", Georgia, serif`;
  ctx.fillStyle = 'rgba(19, 45, 34, 0.52)';
  ctx.fillText(formatPolaroidDate(), width / 2, height - marginBottom * 0.16);
}

const EFFECT_OVERLAYS: Partial<Record<PhotoboothEffectId, OverlayFn>> = {
  garden: (ctx, width, height) => {
    ctx.fillStyle = 'rgba(9, 82, 40, 0.12)';
    ctx.fillRect(0, 0, width, height);
    drawVignette(ctx, width, height, 0.42);
  },
  hearts: (ctx, width, height, time) => drawHeartsOverlay(ctx, width, height, time),
  stars: (ctx, width, height, time) => drawStarsOverlay(ctx, width, height, time),
  carnival: (ctx, width, height, time) => {
    drawCarnivalOverlay(ctx, width, height, time);
    drawPhotoProps(ctx, width, height);
  },
  noir: (ctx, width, height) => drawVignette(ctx, width, height, 0.48),
  dawn: (ctx, width, height) => drawDawnOverlay(ctx, width, height),
  moonlit: (ctx, width, height, time) => drawMoonlitOverlay(ctx, width, height, time),
  champagne: (ctx, width, height, time) => drawChampagneOverlay(ctx, width, height, time),
  film: (ctx, width, height, time) => drawFilmOverlay(ctx, width, height, time),
  pop: (ctx, width, height) => drawPopOverlay(ctx, width, height),
  cyber: (ctx, width, height, time) => drawCyberOverlay(ctx, width, height, time),
  lace: (ctx, width, height) => drawLaceOverlay(ctx, width, height),
  sakura: (ctx, width, height, time) => drawSakuraOverlay(ctx, width, height, time),
  silk: (ctx, width, height, time) => drawSilkOverlay(ctx, width, height, time),
  crown: (ctx, width, height) => drawCrownOverlay(ctx, width, height),
  glitch: (ctx, width, height, time) => drawGlitchOverlay(ctx, width, height, time),
  rainbow: (ctx, width, height, time) => drawRainbowOverlay(ctx, width, height, time),
  disco: (ctx, width, height, time) => drawDiscoOverlay(ctx, width, height, time),
  vhs: (ctx, width, height, time) => drawVhsOverlay(ctx, width, height, time),
  ripple: (ctx, width, height, time) => drawRippleOverlay(ctx, width, height, time),
  prism: (ctx, width, height, time) => drawPrismOverlay(ctx, width, height, time),
  pixel: (ctx, width, height) => drawPixelOverlay(ctx, width, height),
  kaleido: (ctx, width, height) => drawKaleidoOverlay(ctx, width, height),
  pulse: (ctx, width, height, time) => drawPulseOverlay(ctx, width, height, time),
  static: (ctx, width, height, time) => drawStaticOverlay(ctx, width, height, time),
  tunnel: (ctx, width, height, time) => drawTunnelOverlay(ctx, width, height, time),
  shake: (ctx, width, height, time) => drawShakeOverlay(ctx, width, height, time),
  melt: (ctx, width, height, time) => drawMeltOverlay(ctx, width, height, time),
  shard: (ctx, width, height, time) => drawShardOverlay(ctx, width, height, time),
  echo: (ctx, width, height, time) => drawEchoOverlay(ctx, width, height, time),
  rave: (ctx, width, height, time) => drawRaveOverlay(ctx, width, height, time),
  halo: (ctx, width, height, time) => drawHaloOverlay(ctx, width, height, time),
  spiral: (ctx, width, height, time) => drawSpiralOverlay(ctx, width, height, time),
  smear: (ctx, width, height, time) => drawSmearOverlay(ctx, width, height, time),
  vertigo: (ctx, width, height, time) => drawVertigoOverlay(ctx, width, height, time),
  nebula: (ctx, width, height, time) => drawNebulaOverlay(ctx, width, height, time),
  orbit: (ctx, width, height, time) => drawOrbitOverlay(ctx, width, height, time),
};

export function renderPhotoboothFrame(
  ctx: CanvasRenderingContext2D,
  source: CanvasImageSource,
  width: number,
  height: number,
  options: RenderOptions
): void {
  const { mirror, effectId, time = 0 } = options;

  ctx.clearRect(0, 0, width, height);

  if (effectId === 'polaroid') {
    renderPolaroid(ctx, source, width, height, mirror);
    return;
  }

  const filter = EFFECT_FILTERS[effectId];
  ctx.save();
  if (filter) ctx.filter = filter;
  drawMirroredImage(ctx, source, width, height, mirror);
  ctx.filter = 'none';
  ctx.restore();

  const overlay = EFFECT_OVERLAYS[effectId];
  if (overlay) overlay(ctx, width, height, time);
}
